import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';
import { apiClient } from '../api/client';
import { persistedStorage } from '../lib/secureStorage';

export interface User {
  id: string;
  name: string;
  apartment: string;
  email: string;
  is_admin: boolean;
  super_admin?: boolean;
  phone: string | null;
  whatsapp_opt_in: boolean;
  building: { id: string; name: string; timezone: string };
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  pushToken: string | null;
  isPushNotificationsEnabled: boolean;
  maintenanceMode: boolean;
  /** True until the cold-start refresh attempt completes (or is skipped). */
  bootstrapping: boolean;
  setAuth: (accessToken: string, refreshToken: string, user: User) => void;
  setPushToken: (token: string | null) => void;
  setIsPushNotificationsEnabled: (enabled: boolean) => void;
  setMaintenanceMode: (enabled: boolean) => void;
  syncNotificationStatus: () => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * Called once on app mount. If a persisted refresh token exists, attempts
   * a silent refresh so the user lands on home (not login) when the route
   * resolves. Always flips `bootstrapping` to false when done.
   */
  bootstrap: () => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken:  null,
      refreshToken: null,
      user:         null,
      pushToken:    null,
      isPushNotificationsEnabled: false,
      maintenanceMode: false,
      bootstrapping: true,

      setAuth: (accessToken, refreshToken, user) => {
        set({ accessToken, refreshToken, user });
      },

      syncNotificationStatus: async () => {
        try {
          const res = await apiClient.get('/notifications/status');
          const { enabled } = res.data.data as { enabled: boolean };
          set({ isPushNotificationsEnabled: enabled });
        } catch (err) {
          console.warn('[Auth] Failed to fetch notification status:', err);
          set({ isPushNotificationsEnabled: false });
        }
      },

      setPushToken: (token) => {
        set({ pushToken: token });
      },

      setIsPushNotificationsEnabled: (enabled) => {
        set({ isPushNotificationsEnabled: enabled });
      },

      setMaintenanceMode: (enabled) => {
        set({ maintenanceMode: enabled });
      },

      refresh: async () => {
        const { refreshToken } = get();
        const res = await apiClient.post('/auth/refresh', { refresh_token: refreshToken });
        const { access_token, refresh_token, apiVersion } = res.data.data as {
          access_token: string;
          refresh_token: string;
          apiVersion?: string;
        };
        set({ accessToken: access_token, refreshToken: refresh_token });

        // Notify version check if available
        if (typeof window !== 'undefined' && (window as any).__versionCheck) {
          (window as any).__versionCheck.handleAuthResponse(apiVersion);
        }
      },

      bootstrap: async () => {
        const { refreshToken, accessToken } = get();

        // Always hold the splash for at least this long so it feels like a
        // deliberate brand moment instead of a flicker. Runs in parallel with
        // the refresh — the splash dismisses when BOTH have completed.
        const SPLASH_MIN_MS = 2000;
        // Hard ceiling on the splash. If the refresh hasn't resolved by then,
        // release anyway and let the router show login. The refresh keeps
        // running in the background — if it eventually succeeds, tokens
        // populate and the interceptor uses them.
        const BOOTSTRAP_TIMEOUT_MS = 5000;

        const minSplash = new Promise<void>((resolve) => setTimeout(resolve, SPLASH_MIN_MS));

        // Nothing to refresh → just wait out the minimum splash window.
        if (!refreshToken || accessToken) {
          await minSplash;
          set({ bootstrapping: false });
          return;
        }

        let timedOut = false;
        const timeoutPromise = new Promise<void>((resolve) => {
          setTimeout(() => { timedOut = true; resolve(); }, BOOTSTRAP_TIMEOUT_MS);
        });

        const refreshPromise = get().refresh().then(async () => {
          // After a successful refresh, re-fetch the full user profile so the
          // auth store picks up any building fields that were missing or
          // changed since last login (e.g. building.latitude/longitude added
          // server-side — without this, existing sessions kept the truncated
          // shape until their next manual logout/login). Best-effort: any
          // failure leaves the cached user in place rather than wiping it.
          try {
            const me = await apiClient.get<{ data: User }>('/users/me');
            if (!timedOut) set({ user: me.data.data });
          } catch {
            // Silent — the access token works, we just couldn't refresh
            // profile. Next /users/me call (e.g. when the Profile page
            // mounts) will retry.
          }
        }).catch((err) => {
          // Skip side-effects if the timeout already won — splash is gone and
          // we don't want to wipe persisted state from a delayed rejection.
          if (timedOut) return;
          if (axios.isAxiosError(err)) {
            const status = err.response?.status;
            // Definitive auth rejection → clear local auth. Network / 5xx →
            // keep the refresh token so the user can retry later.
            if (status === 401 || status === 403) {
              set({ accessToken: null, refreshToken: null, user: null });
            }
          } else {
            set({ accessToken: null, refreshToken: null, user: null });
          }
        });

        // Wait for refresh-or-timeout AND the minimum splash window.
        await Promise.all([
          Promise.race([refreshPromise, timeoutPromise]),
          minSplash,
        ]);
        set({ bootstrapping: false });
      },

      logout: () => {
        set({ accessToken: null, refreshToken: null, user: null, pushToken: null, isPushNotificationsEnabled: false, maintenanceMode: false, bootstrapping: false });
        window.location.href = '/login';
      },
    }),
    {
      name: 'lotngo-auth',
      // Custom storage: localStorage on the browser, Capacitor Preferences on
      // native. On native first-launch the migration helper (called from
      // main.tsx before hydration) seeds Preferences from the TWA-era
      // localStorage — see lib/secureStorage.ts.
      storage: createJSONStorage(() => persistedStorage),
      // Persist refreshToken, user, and isPushNotificationsEnabled flag — never the accessToken or pushToken
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        user:         state.user,
        isPushNotificationsEnabled: state.isPushNotificationsEnabled,
      }),
      // Skip Zustand's automatic hydration. On native, persistedStorage reads
      // from an in-memory cache that's populated asynchronously by
      // hydratePersistedStorage() in main.tsx — but this module is statically
      // imported by App.tsx and runs BEFORE main.tsx's IIFE awaits the
      // hydration, so an auto-hydrate here would read an empty cache and
      // immediately wipe the refreshToken out from under us. main.tsx calls
      // useAuthStore.persist.rehydrate() AFTER hydratePersistedStorage().
      skipHydration: true,
    },
  ),
);
